import { useEffect, useRef, useState } from 'react';
import './broadcast-player.css';

interface BroadcastItem {
  id?: string;
  title?: string;
  body?: string;
  media_url?: string;
  cta_href?: string;
  cta_label?: string;
  transition?: string;
  is_urgent?: boolean;
  duration_sec?: number;
}

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

export default function BroadcastPlayer() {
  const [items, setItems] = useState<BroadcastItem[]>([]);
  const [current, setCurrent] = useState<BroadcastItem | null>(null);
  const rotTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idxRef = useRef(0);

  async function load() {
    try {
      const res = await fetch(`${CORE_API}/api/v1/social/broadcasts/live`, {
        headers: { 'cache-control': 'no-cache' },
      });
      const json = await res.json().catch(() => ({}));
      setItems(Array.isArray(json?.data) ? json.data : Array.isArray(json?.items) ? json.items : []);
    } catch (error) {
      console.warn('[BroadcastPlayer] failed to load broadcasts', error);
      setItems([]);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (rotTimer.current) clearTimeout(rotTimer.current);
    if (!items.length) {
      setCurrent(null);
      return undefined;
    }

    const urgent = items.find((item) => item.is_urgent);
    if (urgent) {
      setCurrent(urgent);
      return undefined;
    }

    const nonUrgent = items.filter((item) => !item.is_urgent);
    if (!nonUrgent.length) {
      setCurrent(items[0]);
      return undefined;
    }

    const tick = () => {
      const i = (idxRef.current++) % nonUrgent.length;
      const next = nonUrgent[i];
      setCurrent(next);
      const ms = Math.max(3000, (next.duration_sec || 8) * 1000);
      rotTimer.current = setTimeout(tick, ms);
    };

    tick();
    return () => {
      if (rotTimer.current) clearTimeout(rotTimer.current);
    };
  }, [items]);

  if (!current) {
    return <div className="bp-card">No broadcasts yet.</div>;
  }

  const titleText = current.title || '';
  const bodyText = current.body || '';

  return (
    <div className={`bp-root bp--${current.transition || 'fade'}`}>
      {current.media_url ? (
        <img className="bp-media" src={current.media_url} alt={current.title || 'Slide'} />
      ) : (
        <div className="bp-copy">
          {titleText ? (
            <h3 className="bp-title" data-text={titleText}>
              {titleText}
            </h3>
          ) : null}
          {bodyText ? (
            <p className="bp-body" data-text={bodyText}>
              {bodyText}
            </p>
          ) : null}
          {current.cta_href && current.cta_label ? (
            <a className="bp-btn" href={current.cta_href}>
              {current.cta_label}
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
}
